import React, { useState, useEffect } from 'react';
import { TextField, Button, Typography, Grid, Snackbar } from '@mui/material';
import axios from '../config/axiosConfig';

const ProfileDetails = () => {
  const [profile, setProfile] = useState({
    profilePicture: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    publicNickname: ''
  });

  // Store the initial state of nickname to check if user had one before
  const [initialNickname, setInitialNickname] = useState('');

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    publicNickname: ''
  });

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    // Fetch user profile data on component mount
    const fetchProfile = async () => {
      try {
        const response = await axios.get('/api/user/profile');
        setProfile(response.data.data);

        // Store the initial nickname state
        setInitialNickname(response.data.data.publicNickname || '');
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Prevent clearing nickname if it was already set
    if (name === 'publicNickname' && initialNickname && value === '') {
      setErrors({
        ...errors,
        publicNickname: 'Nickname cannot be empty once set. You can change it to a different unique nickname.'
      });
      return; // Don't update the state
    }

    setProfile({ ...profile, [name]: value });

    // Clear error when user starts typing again
    setErrors({
      ...errors,
      [name]: ''
    });

    // Validate phone number
    if (name === 'phone') {
      if (/^\d*$/.test(value)) {
        setErrors({ ...errors, phone: '' }); // Clear error if valid
      } else {
        setErrors({ ...errors, phone: 'Phone number must contain only numbers' });
      }
    }
  };

  const validate = () => {
    let isValid = true;
    const newErrors = {
      firstName: '',
      lastName: '',
      phone: '',
      publicNickname: ''
    };

    if (!profile.firstName.trim()) {
      newErrors.firstName = 'First Name is required';
      isValid = false;
    }

    if (!profile.lastName.trim()) {
      newErrors.lastName = 'Last Name is required';
      isValid = false;
    }

    // Prevent submitting an empty nickname if it was initially set
    if (initialNickname && (!profile.publicNickname || profile.publicNickname.trim() === '')) {
      newErrors.publicNickname = 'Nickname cannot be empty once set';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (validate()) {
      try {
        await axios.put('/api/user/profile', profile);
        setSnackbarSeverity('success');
        setSnackbarMessage('Profile updated successfully');
        setShowSnackbar(true);

        // Check if nickname was changed and refresh the page
        if (profile.publicNickname !== initialNickname) {
          // Wait a moment for the snackbar to be visible before refreshing
          setTimeout(() => {
            window.location.reload();
          }, 1500); // 1.5 seconds delay to show the success message
        }
      } catch (error) {
        console.error('Error updating profile data:', error);

        // Handle specific error for nickname
        if (error.response && error.response.data && error.response.data.field === 'publicNickname') {
          // Use the actual error message from the API
          const errorMessage = error.response.data.error || 'Error with nickname';

          setErrors({
            ...errors,
            publicNickname: errorMessage
          });

          // Use the same error message in the snackbar
          setSnackbarSeverity('error');
          setSnackbarMessage(errorMessage);
        } else {
          setSnackbarSeverity('error');
          setSnackbarMessage('Failed to update profile');
        }
        setShowSnackbar(true);
      }
    }
  };

  return (
    <div>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Profile Details</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={profile.firstName}
            onChange={handleChange}
            error={!!errors.firstName}
            helperText={errors.firstName}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={profile.lastName}
            onChange={handleChange}
            error={!!errors.lastName}
            helperText={errors.lastName}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            value={profile.email}
            onChange={handleChange}
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Public Nickname"
            name="publicNickname"
            value={profile.publicNickname || ''}
            onChange={handleChange}
            error={!!errors.publicNickname}
            helperText={errors.publicNickname || (initialNickname ?
              "This nickname cannot be removed once set, but you can change it" :
              "This name will be displayed to people viewing your public portfolios. Must be at least 4 characters.")}
          />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" color="primary" onClick={handleSave}>Save</Button>
        </Grid>
      </Grid>

      {/* Snackbar Component */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </div>
  );
};

export default ProfileDetails;